import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiPhone, FiMail, FiGlobe, FiStar, FiExternalLink, FiCopy, FiCheck } from 'react-icons/fi';

const PreviewPanel = ({ businessProfile, reviews, media }) => {
  const [activeTab, setActiveTab] = useState('reviews');
  const [copied, setCopied] = useState(false);

  const handleCopyReviewsLink = async () => {
    if (businessProfile?.google_reviews_url) {
      try {
        await navigator.clipboard.writeText(businessProfile.google_reviews_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FiStar key={i} className="h-4 w-4 text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars.push(<FiStar key="half" className="h-4 w-4 text-yellow-400 fill-current opacity-50" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FiStar key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }

    return stars;
  };

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'GBP':
        return 'bg-red-100 text-red-800';
      case 'FreeBusiness':
        return 'bg-green-100 text-green-800';
      case 'WebsiteScrape':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!businessProfile) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20"
    >
      {/* Business Summary Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">{businessProfile.business_name}</h3>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSourceBadgeColor(businessProfile.source_type)}`}>
            {businessProfile.source_type}
          </span>
        </div>

        <div className="space-y-3">
          {businessProfile.address && (
            <div className="flex items-start space-x-3">
              <FiMapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">{businessProfile.address}</span>
            </div>
          )}

          {businessProfile.phone_local && (
            <div className="flex items-center space-x-3">
              <FiPhone className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <span className="text-gray-700">{businessProfile.phone_local}</span>
            </div>
          )}

          {businessProfile.email && (
            <div className="flex items-center space-x-3">
              <FiMail className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <span className="text-gray-700">{businessProfile.email}</span>
            </div>
          )}

          {businessProfile.website_url && (
            <div className="flex items-center space-x-3">
              <FiGlobe className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <a
                href={businessProfile.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 truncate"
              >
                {businessProfile.website_url}
              </a>
            </div>
          )}

          {businessProfile.primary_category && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Category:</span> {businessProfile.primary_category}
            </div>
          )}

          {businessProfile.rating_avg && (
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                {renderStars(businessProfile.rating_avg)}
              </div>
              <span className="text-sm text-gray-600">
                {businessProfile.rating_avg} ({businessProfile.rating_count} reviews)
              </span>
            </div>
          )}

          {businessProfile.description && (
            <div className="text-gray-700">
              <span className="font-medium">Description:</span>
              <p className="mt-1">{businessProfile.description}</p>
            </div>
          )}

          {businessProfile.opening_hours && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Hours:</span> {businessProfile.opening_hours}
            </div>
          )}
        </div>

        <div className="flex space-x-3 mt-6">
          {businessProfile.google_maps_url && (
            <a
              href={businessProfile.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <FiExternalLink className="h-4 w-4" />
              <span>Open in Google Maps</span>
            </a>
          )}

          {businessProfile.google_reviews_url && (
            <button
              onClick={handleCopyReviewsLink}
              className="inline-flex items-center space-x-1 px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              {copied ? <FiCheck className="h-4 w-4" /> : <FiCopy className="h-4 w-4" />}
              <span>{copied ? 'Copied!' : 'Copy Reviews Link'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Details Tabs */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex-1 py-3 px-4 text-sm font-medium text-center border-b-2 transition-colors duration-200 ${
                activeTab === 'reviews'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Reviews ({reviews.length})
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`flex-1 py-3 px-4 text-sm font-medium text-center border-b-2 transition-colors duration-200 ${
                activeTab === 'images'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Images ({media.length})
            </button>
          </nav>
        </div>

        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'reviews' && (
            <div className="space-y-4">
              {reviews.length > 0 ? (
                reviews.map((review, index) => (
                  <div key={index} className="border-b border-gray-100 last:border-b-0 pb-4 last:pb-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-gray-900">{review.author_name}</div>
                      <div className="flex items-center space-x-1">
                        {renderStars(review.rating)}
                        <span className="text-sm text-gray-600 ml-1">{review.rating}</span>
                      </div>
                    </div>
                    {review.text && (
                      <p className="text-gray-700 text-sm">{review.text}</p>
                    )}
                    {review.review_time && (
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(review.review_time).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500">No reviews available</div>
              )}
            </div>
          )}

          {activeTab === 'images' && (
            <div className="grid grid-cols-2 gap-4">
              {media.length > 0 ? (
                media.map((item, index) => (
                  <div key={index} className="relative group">
                    <a
                      href={item.dropbox_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={item.thumbnail_url || item.dropbox_url}
                        alt={`Business photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                        <FiExternalLink className="text-white opacity-0 group-hover:opacity-100 h-6 w-6" />
                      </div>
                    </a>
                    {item.source && (
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getSourceBadgeColor(item.source)}`}>
                          {item.source}
                        </span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center text-gray-500">No images available</div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PreviewPanel;